// src/shared/auth/singletonToken.ts
import { setToCommonDB, getFromCommonDB } from 'src/core/api/cache/idbUtility';

const TOKEN_DB_KEY = 'TOKEN';
const SKEW_MS = 60 * 1000;          // proactive refresh <60s left
const CHANNEL_NAME = 'auth-channel';

const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 8_000;
let _retryBackoffMs = 0;
function _nextBackoff() {
  _retryBackoffMs = _retryBackoffMs ? Math.min(_retryBackoffMs * 2, BACKOFF_MAX_MS) : BACKOFF_MIN_MS;
  return _retryBackoffMs;
}
function _resetBackoff() { _retryBackoffMs = 0; }

const MAX_REFRESH_FAILS = 2;        // cap consecutive failures before logout
const REFRESH_TIMEOUT_MS = 10_000;  // network timeout for refresh fetch

let _consecutiveRefreshFailures = 0;
function _resetFailureCounter() { _consecutiveRefreshFailures = 0; }
function _bumpFailureCounter() { _consecutiveRefreshFailures += 1; }

function _withJitter(ms: number) {
  const factor = 0.5 + Math.random(); // 0.5x–1.5x
  return Math.round(ms * factor);
}

async function _fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = REFRESH_TIMEOUT_MS) {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: ctrl.signal });
  } finally {
    clearTimeout(t);
  }
}
function isHardRefreshFailure(resp: Response | null, body: any): boolean {
  // 1) Explicit auth-ish status codes -> hard
  if (resp) {
    if (resp.status === 400 || resp.status === 401 || resp.status === 403) {
      return true;
    }

    // 5xx etc are treated as soft (server problem), not token problem
    if (resp.status >= 500) return false;
  }

  // 2) “Success” response but no access_token -> assume backend rejected RT
  if (resp && resp.ok && (!body || !body.access_token)) {
    return true;
  }

  // Everything else default to soft
  return false;
}

function isNetworkLikeError(err: any): boolean {
  if (!err) return false;
  if (err.name === 'AbortError') return true; // timeout / aborted
  if (err.name === 'TypeError') return true;  // fetch network error in many browsers

  // Extra: if browser says offline, treat as network
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  return false;
}
// --- BASE_URL trailing-slash safe ---
const RAW_BASE = process.env.REACT_APP_BOLD_API_BASE_URL || '';
const BASE_URL = RAW_BASE.endsWith('/') ? RAW_BASE.slice(0, -1) : RAW_BASE;

let _tokenUrl: string = `${BASE_URL}/token`;
let _clientAuthHeader: string = process.env.REACT_APP_BOLD_CLIENT_AUTHENTICATION ?? '';

export function configureAuthEndpoints(opts: { tokenUrl?: string; clientAuthHeader?: string } = {}) {
  if (opts.tokenUrl) _tokenUrl = opts.tokenUrl;
  if (typeof opts.clientAuthHeader === 'string') _clientAuthHeader = opts.clientAuthHeader;
}

type TokenObject =
  | {
    access_token: string | null;
    refresh_token: string | null;
    expires_in?: number | string;
    expMs?: number;
    expires_at_ms?: number;
  }
  | null
  | undefined;

let _accessToken: string | null = null;
let _refreshToken: string | null = null;
let _expMs: number | null = null;
let _initialized = false;

let _inFlightRefresh: Promise<boolean> | null = null;
let _refreshTimerId: ReturnType<typeof setTimeout> | null = null;

const _listeners: Record<'updated' | 'loggedOut' | 'refreshing', Set<() => void>> = { updated: new Set(), loggedOut: new Set(), refreshing: new Set(), };
function _emit(event: 'updated' | 'loggedOut' | 'refreshing') {
  _listeners[event].forEach((fn) => { try { fn(); } catch { } });
}

function _broadcast(msg: any) {
  if (_bc) _bc.postMessage(msg);
  else if (typeof window !== 'undefined') {
    localStorage.setItem(`${CHANNEL_NAME}:version`, String(Date.now()));
  }
}

let _bc: BroadcastChannel | null = null;
try {
  _bc = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;
  if (_bc) {
    _bc.onmessage = (ev: MessageEvent) => {
      const data = (ev.data || {}) as { type?: string; payload?: any };
      if (data.type === 'tokensUpdated') {
        _applyTokens(data.payload as TokenObject, { persist: false, broadcast: false, schedule: true });
        _emit('updated');
      } else if (data.type === 'loggedOut') {
        _applyTokens(null, { persist: false, broadcast: false, schedule: false });
        _emit('loggedOut');
      }
    };
  } else if (typeof window !== 'undefined') {
    let _debounce: ReturnType<typeof setTimeout> | undefined;
    window.addEventListener('storage', (e: StorageEvent) => {
      if (e.key === `${CHANNEL_NAME}:version`) {
        if (_debounce) clearTimeout(_debounce);
        _debounce = setTimeout(() => { hydrateFromDB(); }, 40);
      }
    });
  }
} catch { }

function _clearTimer() { if (_refreshTimerId) { clearTimeout(_refreshTimerId); _refreshTimerId = null; } }

function _scheduleProactiveRefresh(overrideDelayMs?: number) {
  _clearTimer();

  // small guard: only schedule if we still have a refresh token and an expiry
  if (!_refreshToken || typeof _expMs !== 'number') return;
  if (typeof overrideDelayMs === 'number') {
    _refreshTimerId = setTimeout(() => {
      if (!_refreshToken) return;
      refreshIfNeeded({ reason: 'scheduled-backoff' }).catch(() => { });
    }, Math.max(overrideDelayMs, 0));
    return;
  }
  const now = Date.now();
  const msLeft = _expMs - now;
  const fireIn = Math.max(msLeft - SKEW_MS, 0);
  _refreshTimerId = setTimeout(() => {
    if (!_refreshToken) return;
    refreshIfNeeded({ reason: 'scheduled' }).catch(() => { });
  }, fireIn);
}

function _applyTokens(tokenObj: TokenObject, { persist = true, broadcast = true, schedule = true }: { persist?: boolean; broadcast?: boolean; schedule?: boolean } = {}) {
  if (tokenObj === undefined) return;

  if (tokenObj === null) {
    _accessToken = null;
    _refreshToken = null;
    _expMs = null;
    _clearTimer();
    _resetBackoff();
    _resetFailureCounter();

    if (persist) {
      setToCommonDB(TOKEN_DB_KEY, null).finally(() => {
        if (broadcast) _broadcast({ type: 'loggedOut' });
      });
    } else {
      if (broadcast) _broadcast({ type: 'loggedOut' });
    }
    return;
  }

  const { access_token, refresh_token, expMs, expires_in, expires_at_ms } = tokenObj as NonNullable<TokenObject>;
  _accessToken = access_token || null;
  _refreshToken = refresh_token || null;

  const now = Date.now();
  const expiresInNum =
    typeof expires_in === 'string' ? parseInt(expires_in, 10) : typeof expires_in === 'number' ? expires_in : undefined;

  // normalize expMs once; scheduler will subtract SKEW_MS
  const computedExpMs =
    typeof expMs === 'number'
      ? expMs
      : typeof expires_at_ms === 'number'
        ? expires_at_ms
        : typeof expiresInNum === 'number'
          ? now + expiresInNum * 1000
          : null;

  _expMs = typeof computedExpMs === 'number' ? computedExpMs : null;

  _resetFailureCounter();
  _resetBackoff();

  if (persist) {
    setToCommonDB(TOKEN_DB_KEY, tokenObj).finally(() => {
      if (broadcast) _broadcast({ type: 'tokensUpdated', payload: tokenObj });
    });
  } else {
    if (broadcast) _broadcast({ type: 'tokensUpdated', payload: tokenObj });
  }

  if (schedule) _scheduleProactiveRefresh();
}

function formEncode(data: Record<string, string>): string {
  return Object.entries(data).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
}

export function getAuthToken(): string | null { return _accessToken; }
export function getRefreshToken(): string | null { return _refreshToken; }
export function getExpiryMs(): number | null { return _expMs; }
export function isInitialized(): boolean { return _initialized; }

export function setTokens(tokenObj: NonNullable<TokenObject>) {
  _applyTokens(tokenObj, { persist: true, broadcast: true, schedule: true });
  _emit('updated');
}

export function clearTokens() {
  _applyTokens(null, { persist: true, broadcast: true, schedule: false });
  _emit('loggedOut');
}

export function on(event: 'updated' | 'loggedOut' | 'refreshing', handler: () => void) {
  _listeners[event]?.add(handler);
  return () => _listeners[event]?.delete(handler);
}

export async function hydrateFromDB() {
  try {
    const tokenObj = await getFromCommonDB<TokenObject>(TOKEN_DB_KEY);
    // IMPORTANT: only apply if it's a real token object (not undefined, not null)
    if (tokenObj && typeof tokenObj === 'object') {
      _applyTokens(tokenObj, { persist: false, broadcast: false, schedule: true });
    }
  } finally {
    _initialized = true;
  }
}

export async function initializeTokenManager(initialTokenFromCache: TokenObject = null) {
  if (initialTokenFromCache) {
    _applyTokens(initialTokenFromCache, { persist: false, broadcast: false, schedule: true });
    _initialized = true;
  } else {
    await hydrateFromDB();
  }
  if (typeof window !== 'undefined') {
    const warm = () => refreshIfNeeded({ reason: 'visibility' }).catch(() => { });
    window.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') warm();
    });
    window.addEventListener('focus', warm);

    // --- ADDITION: Retry instantly when network reconnects ---
    window.addEventListener('online', () => { refreshIfNeeded({ reason: 'online' }).catch(() => { }); });
    // ---------------------------------------------------------
  }
}
export async function refreshIfNeeded({ force = false }: { force?: boolean; reason?: string } = {}): Promise<boolean> {
  const now = Date.now();
  const timeLeft = (_expMs ?? 0) - now;

  if (!force) {
    if (!_refreshToken) return false;
    if (typeof _expMs === 'number' && timeLeft > SKEW_MS) return false;
  } else {
    if (!_refreshToken) return false;
  }

  if (_inFlightRefresh) return _inFlightRefresh;

  _emit('refreshing');
  const attemptedRefreshToken = _refreshToken;

  // ---------------------------
  // CLASSIFIERS AVAILABLE TO ALL BLOCKS
  // ---------------------------

  const classifyHard = (respObj: Response | null, json: any): boolean => {
    if (respObj) {
      // backend tells us the token is bad
      if (respObj.status === 400 || respObj.status === 401 || respObj.status === 403) return true;

      // server errors should NOT log user out
      if (respObj.status >= 500) return false;
    }

    // success response but missing access token
    if (respObj && respObj.ok && (!json || !json.access_token)) return true;
    return false;
  };

  const classifyNetwork = (err: any): boolean => {
    if (!err) return false;
    if (err.name === "AbortError") return true;
    if (err.name === "TypeError") return true;  // fetch network error
    if (typeof navigator !== "undefined" && navigator.onLine === false)
      return true;
    return false;
  };

  // ---------------------------

  _inFlightRefresh = (async () => {
    try {
      // PRE-OFFLINE CHECK
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const delay = _withJitter(_nextBackoff());
        _scheduleProactiveRefresh(delay);
        return false;
      }
      const body = formEncode({ grant_type: "refresh_token", refresh_token: attemptedRefreshToken as string, });
      const headers: Record<string, string> = { "Content-Type": "application/x-www-form-urlencoded", };
      if (_clientAuthHeader) headers.Authorization = _clientAuthHeader;
      const resp = await _fetchWithTimeout(_tokenUrl, { method: "POST", headers, body }, REFRESH_TIMEOUT_MS);

      // refreshToken changed mid-flight → ignore
      if (_refreshToken !== attemptedRefreshToken) {
        _resetFailureCounter();
        _resetBackoff();
        return true;
      }

      // NON-OK RESPONSE
      if (!resp.ok) {
        const hard = classifyHard(resp, null);
        if (hard) {
          _bumpFailureCounter();
          if (_consecutiveRefreshFailures >= MAX_REFRESH_FAILS) {
            if (_refreshToken !== attemptedRefreshToken) {
              _resetFailureCounter();
              _resetBackoff();
              return true;
            }
            clearTokens();
            return false;
          }
        }

        // soft failure → retry later
        const delay = _withJitter(_nextBackoff());
        _scheduleProactiveRefresh(delay);
        return false;
      }

      // PARSE BODY
      let res: any = null;
      try {
        res = await resp.json();
      } catch {
        const hard = classifyHard(resp, null);
        if (hard) {
          _bumpFailureCounter();
          if (_consecutiveRefreshFailures >= MAX_REFRESH_FAILS) {
            if (_refreshToken !== attemptedRefreshToken) {
              _resetFailureCounter();
              _resetBackoff();
              return true;
            }
            clearTokens();
            return false;
          }
        }
        const delay = _withJitter(_nextBackoff());
        _scheduleProactiveRefresh(delay);
        return false;
      }

      // MISSING TOKEN IN BODY
      if (!res || !res.access_token) {
        const hard = classifyHard(resp, res);
        if (hard) {
          _bumpFailureCounter();
          if (_consecutiveRefreshFailures >= MAX_REFRESH_FAILS) {
            if (_refreshToken !== attemptedRefreshToken) {
              _resetFailureCounter();
              _resetBackoff();
              return true;
            }
            clearTokens();
            return false;
          }
        }
        const delay = _withJitter(_nextBackoff());
        _scheduleProactiveRefresh(delay);
        return false;
      }

      // SUCCESS
      _resetFailureCounter();
      _resetBackoff();
      const rawExpiresIn = res.expires_in ?? 0;
      const expiresInNum =
        typeof rawExpiresIn === "string" ? parseInt(rawExpiresIn, 10) : rawExpiresIn;
      const tokenObj = {
        access_token: res.access_token as string,
        refresh_token: (res.refresh_token as string) ?? _refreshToken,
        expires_in: expiresInNum,
        expMs:
          (typeof res.expMs === "number" && res.expMs) ||
          Date.now() + (expiresInNum ?? 0) * 1000,
      };
      setTokens(tokenObj);
      return true;
    } catch (err) {
      // NETWORK FAILURE BLOCK
      if (_refreshToken !== attemptedRefreshToken) {
        _resetFailureCounter();
        _resetBackoff();
        return true;
      }
      if (classifyNetwork(err)) {
        const delay = _withJitter(_nextBackoff());
        _scheduleProactiveRefresh(delay);
        return false;
      }

      // UNKNOWN HARD FAILURE
      _bumpFailureCounter();
      if (_consecutiveRefreshFailures >= MAX_REFRESH_FAILS) {
        if (_refreshToken !== attemptedRefreshToken) {
          _resetFailureCounter();
          _resetBackoff();
          return true;
        }
        clearTokens();
        return false;
      }
      const delay = _withJitter(_nextBackoff());
      _scheduleProactiveRefresh(delay);
      return false;
    } finally {
      _inFlightRefresh = null;
    }
  })();

  return _inFlightRefresh;
}
